export default function TenantRetentionStrategies() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-user-heart-line"></i>
            <span>Tenant Relations</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Tenant Retention Strategies During the Sale Process
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Keeping good tenants happy and in place during your sale maximizes property value and makes your building more attractive to buyers.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/d20b916cbd92d2bf85680586a67fbbd3.jpg"
                alt="Tenant retention strategies and relationship building during apartment sale"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Why Tenant Retention Matters During Sales</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              High tenant turnover during the sale process sends negative signals to buyers about property quality, management, and tenant satisfaction. Every vacant unit reduces your NOI and can decrease property value by $20,000-$50,000 or more. Retaining tenants should be a top priority from the moment you decide to sell.
            </p>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-line-chart-line text-green-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Value Impact of Retention</h4>
                  <p className="text-green-800 text-sm leading-relaxed">
                    A 12-unit building with 95% occupancy vs. 75% occupancy can see a $150,000-$300,000 difference in sale price. Every tenant you keep adds directly to your bottom line.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Effective Retention Strategies</h3>

            <div className="space-y-4">
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-chat-smile-3-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Maintain Excellent Communication</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Keep tenants informed about the sale process, reassure them about lease protections, and respond promptly to all inquiries. Uncertainty causes tenants to look for new housing.
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-mail-line text-teal-600"></i>
                        <span>Regular updates</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-phone-line text-teal-600"></i>
                        <span>Quick responses</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-fill text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Accelerate Maintenance and Repairs</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Address all maintenance requests immediately. Show tenants you're still invested in their comfort and the property's condition, even though you're selling.
                    </p>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-600">
                        <strong>Goal:</strong> 24-48 hour response time on all requests during sale period
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-gift-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Offer Retention Incentives</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Consider small incentives for tenants who cooperate with showings and stay through closing. This is much cheaper than vacancy costs.
                    </p>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Incentive Ideas:</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li>• $100-$200 rent credit for showing cooperation</li>
                        <li>• Free carpet cleaning or unit upgrades</li>
                        <li>• Gift cards for local restaurants or stores</li>
                        <li>• Waived late fees or parking fees</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Provide Lease Security Assurances</h4>
                    <p className="text-slate-600 text-sm">
                      Give tenants written confirmation that their leases will be honored by the new owner and that their rights are protected by law. This reduces anxiety and move-out risk.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-star-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Introduce Tenants to New Owner</h4>
                    <p className="text-slate-600 text-sm">
                      When appropriate, facilitate introductions between tenants and the buyer. This builds relationships early and reduces tenant uncertainty about new ownership.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Minimize Showing Disruption</h4>
                    <p className="text-slate-600 text-sm">
                      Schedule showings efficiently, group them when possible, and respect tenant schedules. Excessive disruption is a primary reason tenants leave during sales.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Tenant Retention Cost-Benefit Analysis</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Scenario</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Cost</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Value Impact</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Net Benefit</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Tenant leaves (1 unit vacant)</td>
                  <td className="py-4 px-4 text-red-600">-$1,500/month rent</td>
                  <td className="py-4 px-4 text-red-600">-$30,000 to -$50,000</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                      <i className="ri-arrow-down-line"></i>
                      -$30K-$50K
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Retention incentive ($200 credit)</td>
                  <td className="py-4 px-4 text-slate-600">$200</td>
                  <td className="py-4 px-4 text-green-600">Maintains full value</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                      <i className="ri-arrow-up-line"></i>
                      +$29.8K-$49.8K
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Accelerated maintenance ($500)</td>
                  <td className="py-4 px-4 text-slate-600">$500</td>
                  <td className="py-4 px-4 text-green-600">Prevents tenant departure</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                      <i className="ri-arrow-up-line"></i>
                      +$29.5K-$49.5K
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Multiple tenants leave (3 units)</td>
                  <td className="py-4 px-4 text-red-600">-$4,500/month rent</td>
                  <td className="py-4 px-4 text-red-600">-$90,000 to -$150,000</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                      <i className="ri-arrow-down-line"></i>
                      -$90K-$150K
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-700">Comprehensive retention program</td>
                  <td className="py-4 px-4 text-slate-600">$2,000-$3,000 total</td>
                  <td className="py-4 px-4 text-green-600">Maintains 95%+ occupancy</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                      <i className="ri-arrow-up-line"></i>
                      +$87K-$147K
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-slate-600 mt-6 italic">
            * Value impacts based on typical cap rate calculations where 1% occupancy loss = $20,000-$40,000 value reduction
          </p>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Warning Signs of Tenant Flight Risk</h3>
          
          <p className="text-slate-700 mb-6 leading-relaxed">
            Watch for these indicators that tenants may be planning to leave during your sale process. Early intervention can prevent departures.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-question-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Excessive Questions About Sale</h4>
              <p className="text-slate-600 text-sm">
                Tenants repeatedly asking about new owner, lease changes, or rent increases may be considering moving. Address concerns proactively.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-close-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Lease Non-Renewal Inquiries</h4>
              <p className="text-slate-600 text-sm">
                Tenants asking about early termination, lease break fees, or move-out procedures are likely planning to leave.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-emotion-unhappy-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Showing Complaints</h4>
              <p className="text-slate-600 text-sm">
                Tenants complaining about showing frequency or disruption may be frustrated enough to move. Adjust showing schedule immediately.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Increased Maintenance Requests</h4>
              <p className="text-slate-600 text-sm">
                Sudden spike in repair requests may indicate tenant dissatisfaction or preparation for move-out inspection.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-unfollow-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Reduced Communication</h4>
              <p className="text-slate-600 text-sm">
                Tenants who stop responding to messages or become difficult to reach may be disengaging before departure.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-truck-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Moving Company Inquiries</h4>
              <p className="text-slate-600 text-sm">
                If you notice moving trucks or boxes, or tenants ask about move-out procedures, they're likely planning to leave soon.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
